function asUpperText(value) {
  return String(value || "").trim().toUpperCase();
}

function hasCyrillic(value) {
  return /[А-Яа-яЁё]/.test(String(value || ""));
}

function normalizeToken(value) {
  return asUpperText(value).replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function extractErrorTokens(error) {
  const values = [
    error?.code,
    error?.error,
    error?.message,
    error?.errorCode,
    error?.type,
  ];

  return [...new Set(
    values
      .map((value) => normalizeToken(value))
      .filter(Boolean)
  )];
}

function isTechnicalMessage(rawMessage, tokens = []) {
  const raw = String(rawMessage || "").trim();
  const upper = raw.toUpperCase();
  if (!raw) return true;
  if (tokens.some((token) => token && upper === token)) return true;
  if (/^HTTP\s*\d+$/i.test(raw)) return true;
  if (/^[A-Z0-9_]{6,}$/.test(raw)) return true;
  return (
    upper.includes("DATABASE ERROR") ||
    upper.includes("INTERNAL SERVER ERROR") ||
    upper.includes("BAD REQUEST") ||
    upper.includes("SERVICE UNAVAILABLE") ||
    upper.includes("TIMEOUT") ||
    upper.includes("FORBIDDEN") ||
    upper.includes("UNAUTHORIZED") ||
    upper.includes("SQL")
  );
}

export function resolveApiErrorMessage(error, fallback, { tokenMessages = {}, statusMessages = {} } = {}) {
  const status = Number(error?.status || 0) || 0;
  const rawMessage = String(error?.message || "").trim();
  const rawError = String(error?.error || "").trim();
  const tokens = extractErrorTokens(error);

  for (const token of tokens) {
    if (tokenMessages[token]) return tokenMessages[token];
  }

  if (statusMessages[status]) return statusMessages[status];

  if (rawMessage && hasCyrillic(rawMessage) && !isTechnicalMessage(rawMessage, tokens)) {
    return rawMessage;
  }

  if (rawError && hasCyrillic(rawError) && !isTechnicalMessage(rawError, tokens)) {
    return rawError;
  }

  return fallback;
}

export function resolveClientsErrorMessage(error, fallback = "Не удалось загрузить клиентов") {
  return resolveApiErrorMessage(error, fallback, {
    tokenMessages: {
      DATABASE_ERROR: "Не удалось загрузить клиентов. Сервер временно недоступен, попробуйте ещё раз чуть позже.",
      INTERNAL_SERVER_ERROR: "Не удалось загрузить клиентов. Сервер временно недоступен, попробуйте ещё раз чуть позже.",
      FORBIDDEN: "У вас нет доступа к разделу клиентов.",
      ACCESS_DENIED: "У вас нет доступа к разделу клиентов.",
      PSYCHOLOGIST_CLIENTS_ACCESS_DENIED: "У вас нет доступа к разделу клиентов.",
    },
    statusMessages: {
      401: "Нужно заново войти в аккаунт, чтобы открыть клиентов.",
      403: "У вас нет доступа к разделу клиентов.",
      404: "Раздел клиентов временно недоступен.",
      429: "Слишком много запросов. Попробуйте открыть клиентов чуть позже.",
      500: "Не удалось загрузить клиентов. Сервер временно недоступен, попробуйте ещё раз чуть позже.",
      502: "Не удалось загрузить клиентов. Сервер временно недоступен, попробуйте ещё раз чуть позже.",
      503: "Не удалось загрузить клиентов. Сервер временно недоступен, попробуйте ещё раз чуть позже.",
      504: "Не удалось загрузить клиентов. Сервер временно недоступен, попробуйте ещё раз чуть позже.",
    },
  });
}

export function resolveCalendarErrorMessage(
  error,
  {
    provider = "Google Calendar",
    fallback = `Не удалось выполнить операцию для ${provider}`,
  } = {}
) {
  const providerName = String(provider || "Календарь").trim();
  const syncFetchText = providerName === "Google Calendar"
    ? "Не удалось получить события из Google Calendar. Проверьте подключение аккаунта и повторите синхронизацию."
    : "Не удалось получить события из Яндекс Календаря. Проверьте подключение аккаунта и повторите синхронизацию.";
  const permissionText = providerName === "Google Calendar"
    ? "Google Calendar не дал доступ к календарям. Подключите интеграцию заново."
    : "Яндекс Календарь не дал доступ к календарям. Подключите интеграцию заново.";

  return resolveApiErrorMessage(error, fallback, {
    tokenMessages: {
      GOOGLE_CALENDAR_EVENTS_FETCH_FAILED: "Не удалось получить события из Google Calendar. Проверьте подключение аккаунта и повторите синхронизацию.",
      YANDEX_CALENDAR_EVENTS_FETCH_FAILED: "Не удалось получить события из Яндекс Календаря. Проверьте подключение аккаунта и повторите синхронизацию.",
      GOOGLE_CALENDAR_SYNC_FAILED: "Не удалось завершить синхронизацию Google Calendar. Попробуйте ещё раз чуть позже.",
      YANDEX_CALENDAR_SYNC_FAILED: "Не удалось завершить синхронизацию Яндекс Календаря. Попробуйте ещё раз чуть позже.",
      GOOGLE_CALENDAR_ACCESS_DENIED: permissionText,
      YANDEX_CALENDAR_ACCESS_DENIED: permissionText,
      GOOGLE_CALENDAR_PERMISSION_DENIED: permissionText,
      YANDEX_CALENDAR_PERMISSION_DENIED: permissionText,
      GOOGLE_CALENDAR_NOT_CONNECTED: `${providerName} ещё не подключён.`,
      YANDEX_CALENDAR_NOT_CONNECTED: `${providerName} ещё не подключён.`,
      CALENDAR_NOT_CONNECTED: `${providerName} ещё не подключён.`,
      DATABASE_ERROR: `Сейчас не удалось обратиться к ${providerName}. Попробуйте ещё раз чуть позже.`,
      INTERNAL_SERVER_ERROR: `Сейчас не удалось обратиться к ${providerName}. Попробуйте ещё раз чуть позже.`,
    },
    statusMessages: {
      401: `Нужно заново войти в аккаунт, чтобы работать с ${providerName}.`,
      403: permissionText,
      404: `${providerName} временно недоступен на сервере.`,
      429: `Слишком много запросов к ${providerName}. Попробуйте ещё раз чуть позже.`,
      500: `Сейчас не удалось обратиться к ${providerName}. Попробуйте ещё раз чуть позже.`,
      502: `Сейчас не удалось обратиться к ${providerName}. Попробуйте ещё раз чуть позже.`,
      503: `Сейчас не удалось обратиться к ${providerName}. Попробуйте ещё раз чуть позже.`,
      504: syncFetchText,
    },
  });
}

export function buildUnavailableSectionsNotice(sections = []) {
  const list = Array.isArray(sections) ? sections.filter(Boolean) : [];
  const sectionLabels = {
    profile: "профиль",
    clients: "данные по клиентам",
    schedule: "расписание",
  };

  const normalized = list
    .map((item) => String(item || "").trim().toLowerCase())
    .filter(Boolean)
    .map((item) => sectionLabels[item] || item);

  if (!normalized.length) {
    return {
      title: "",
      message: "",
    };
  }

  if (normalized.length === 1) {
    return {
      title: "Часть данных временно недоступна",
      message: `Не удалось загрузить ${normalized[0]}. Остальные разделы продолжают работать.`,
    };
  }

  return {
    title: "Часть данных временно недоступна",
    message: `Не удалось загрузить некоторые разделы: ${normalized.join(", ")}.`,
  };
}
